import { useEffect, type JSX } from 'react';

import { Box } from '@mui/material';
import { Outlet } from 'react-router-dom';

import { useAppDispatch } from '@/store/hooks';
import { loadDispatchers } from '@/store/dispatchersSlice';
import { loadUsers } from '@/store/usersSlice';

import { Topbar } from './shell';

// App shell (ADR-0035 — top-navigation, reverses the ADR-0029 §5 sidebar):
// flex-column { Topbar, main { Outlet } }. The sole nav chrome is the sticky
// Topbar; content is full-width below it (no sidebar, no collapse). The page
// title/breadcrumb band was removed app-wide (change-request
// `remove-page-header-band`) — pages render their own toolbars/content
// directly under the Topbar.
//
// Boot-time side-effect (PRESERVED verbatim): fetch the dispatchers roster AND
// the full KC users roster in parallel on mount so the orders UI can resolve
// every `assignedDispatcherSub` UUID into a human name regardless of role.
export const AppLayout = (): JSX.Element => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    void dispatch(loadDispatchers());
    void dispatch(loadUsers());
  }, [dispatch]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Definite viewport height (not min) so `main` gets a bounded height
        // and a routed page can establish its OWN internal scroll
        // (CHR-01 orders independent-scroll columns). `overflow:hidden` keeps
        // the root from ever scrolling as a unit — the Topbar stays put and
        // either `main` (normal pages) or the page's own panes (orders) own
        // the scroll.
        height: '100vh',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <Topbar />
      <Box
        component="main"
        sx={(th) => ({
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // Normal pages scroll inside `main`. The orders page instead fills
          // this bounded height (`flex:1; minHeight:0; overflow:hidden`) and
          // gives its two columns their own scrollbars, so `overflow:auto`
          // here is a no-op for it (nothing overflows `main`).
          overflow: 'auto',
          // DESIGN.md §4 — section padding 20px 24px 32px (top/horizontal/bottom).
          pt: th.spacing(2.5),
          px: th.spacing(3),
          pb: th.spacing(4),
        })}
      >
        <Outlet />
      </Box>
    </Box>
  );
};
